import * as THREE from 'three';
import type { World } from '../world/World';
import type { Player } from '../player/Player';
import type { CreatureManager } from '../entities/CreatureManager';
import { itemDef } from '../data/items';
import { rollLoot } from '../data/loot';
import { mulberry32, hashString } from '../core/Rng';

/**
 * Savaş sistemi.
 * Yakın dövüş (kılıç/alet/yumruk) ve yay ile menzilli saldırı.
 * Hasar ve loot zorluk çarpanlarını kullanır; ölüm efektleri kan/parçalanma
 * içermez (duman + parçacık + ses tasarımı).
 */

export interface AttackResult {
  hit: boolean;
  killed?: boolean;
  targetId?: string;
  targetName?: string;
  damage?: number;
  loot?: Array<{ item: string; count: number }>;
  kogox?: number;
}

const MELEE_RANGE = 3.2;

/** Oyuncunun yakın dövüş saldırısı. */
export function meleeAttack(world: World, player: Player, creatures: CreatureManager, authoritative = true): AttackResult {
  const origin = player.eyePosition;
  const dir = player.forward();
  const hit = creatures.pick(origin, dir, MELEE_RANGE);
  if (!hit) return { hit: false };

  const damage = player.attackDamage();
  if (!authoritative) return { hit: true, killed: false, targetId: hit.creature.id, targetName: hit.creature.def.name, damage };
  const res = creatures.damage(hit.creature.id, damage, true);
  // Silah/alet aşınır
  const wear = Math.max(1, Math.round(world.params.difficulty.durabilityWear));
  player.inventory.wearHeld(wear);

  if (!res.killed) {
    return { hit: true, killed: false, targetId: hit.creature.id, targetName: hit.creature.def.name, damage };
  }
  const loot = grantLoot(world, player, hit.creature.def.lootTable, hit.creature.id);
  return { hit: true, killed: true, targetId: hit.creature.id, targetName: hit.creature.def.name, damage, loot: loot.items, kogox: loot.kogox };
}

/** Loot tablosunu authoritative dünya seed'iyle deterministik olarak çevirir. */
export function rollCreatureLoot(
  world: World,
  tableId: string,
  seedKey: string,
): { items: Array<{ item: string; count: number }>; kogox: number } {
  const rand = mulberry32(hashString(seedKey) ^ world.params.seed);
  return rollLoot(tableId, rand, world.params.difficulty.lootBonus);
}

/** Loot tablosunu çevirir ve envantere ekler. */
export function grantLoot(
  world: World,
  player: Player,
  tableId: string,
  seedKey: string,
): { items: Array<{ item: string; count: number }>; kogox: number } {
  const result = rollCreatureLoot(world, tableId, seedKey);
  const given: Array<{ item: string; count: number }> = [];
  for (const entry of result.items) {
    const left = player.inventory.add(entry.item, entry.count);
    if (left < entry.count) given.push({ item: entry.item, count: entry.count - left });
  }
  if (result.kogox > 0) player.kogox += result.kogox;
  return { items: given, kogox: result.kogox };
}

/** Yay: gerilme süresine göre hasar ve hız. */
export interface Arrow {
  mesh: THREE.Mesh;
  dir: THREE.Vector3;
  speed: number;
  damage: number;
  life: number;
}

const arrowGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.9, 5);
arrowGeo.rotateX(Math.PI / 2);
const arrowMat = new THREE.MeshBasicMaterial({ color: 0xd8c9a0 });

export function fireArrow(world: World, player: Player, drawRatio: number): Arrow | null {
  const held = player.inventory.held();
  const tool = held ? itemDef(held.item).tool : null;
  if (!tool || tool.class !== 'bow') return null;
  if (!player.inventory.has('arrow', 1)) return null;
  player.inventory.remove('arrow', 1);
  player.inventory.wearHeld(1);

  const power = 0.35 + drawRatio * 0.65;
  const mesh = new THREE.Mesh(arrowGeo, arrowMat);
  mesh.position.copy(player.eyePosition);
  const dir = player.forward().clone();
  mesh.lookAt(mesh.position.clone().add(dir));
  world.scene.add(mesh);
  return {
    mesh,
    dir,
    speed: 34 + power * 26,
    damage: tool.damage * power * player.stats.modifier('strength'),
    life: 3,
  };
}

/** Uçuşan okları ilerletir; isabet ederse hasar uygular. */
export function updateArrows(
  arrows: Arrow[],
  dt: number,
  world: World,
  creatures: CreatureManager,
  onHit: (name: string, killed: boolean, loot: Array<{ item: string; count: number }> | undefined, targetId: string, damage: number) => void,
  player: Player,
  authoritative = true,
): void {
  for (let i = arrows.length - 1; i >= 0; i--) {
    const a = arrows[i]!;
    // Yerçekimi ile hafif düşüş: menzil hissi oluşsun
    a.dir.y -= 0.55 * dt;
    a.dir.normalize();
    const step = a.speed * dt;
    a.mesh.position.addScaledVector(a.dir, step);
    a.mesh.lookAt(a.mesh.position.clone().add(a.dir));
    a.life -= dt;

    const hit = creatures.pick(a.mesh.position, a.dir, step + 1.4);
    const ground = world.heightAt(a.mesh.position.x, a.mesh.position.z);
    if (hit) {
      let killed = false;
      let loot: Array<{ item: string; count: number }> | undefined;
      if (authoritative) {
        const res = creatures.damage(hit.creature.id, a.damage, true);
        killed = res.killed;
        if (killed) loot = grantLoot(world, player, hit.creature.def.lootTable, hit.creature.id).items;
      }
      onHit(hit.creature.def.name, killed, loot, hit.creature.id, a.damage);
      removeArrow(arrows, i, world);
      continue;
    }
    if (a.life <= 0 || a.mesh.position.y < ground) {
      removeArrow(arrows, i, world);
    }
  }
}

function removeArrow(arrows: Arrow[], index: number, world: World): void {
  const a = arrows[index]!;
  world.scene.remove(a.mesh);
  arrows.splice(index, 1);
}
